import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  getAggregateSpend,
  type AdminAggregateFilters,
  type AdminAggregateGroupBy,
  type AdminSpendAggregateResponse,
} from "@admin/lib/api";
import { formatCents } from "@admin/lib/utils";
import { Button } from "@admin/components/ui/button";
import { Input } from "@admin/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@admin/components/ui/card";
import { CATEGORY_COLORS, DailyStackedChart, type SeriesDef } from "@admin/components/charts/SpendCharts";

const GROUP_BY: AdminAggregateGroupBy[] = ["day", "category", "model", "tool", "user", "country"];

const SELECT_CLASS =
  "h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg focus:outline-none focus:ring-1 focus:ring-accent";

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-fg">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

const DAILY_SERIES: SeriesDef[] = [{ key: "cost", label: "Cost", color: CATEGORY_COLORS.llm }];

function formatUsdCost(value: number | null | undefined): string {
  if (value == null) return "-";
  return `$${value.toFixed(2)}`;
}

function formatCredits(value: number | null | undefined): string {
  if (value == null) return "-";
  return new Intl.NumberFormat("en-US").format(value);
}

export default function SpendPage() {
  const [filters, setFilters] = useState<AdminAggregateFilters>({ groupBy: "category" });
  const [data, setData] = useState<AdminSpendAggregateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = async (f: AdminAggregateFilters) => {
    setLoading(true);
    setErr(null);
    try {
      setData(await getAggregateSpend(f));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load spend");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dailyData = useMemo(
    () => (data?.daily ?? []).map((d) => ({ day: d.day, cost: d.costCents })),
    [data],
  );

  const margin = data ? data.totals.priceCents - data.totals.costCents : 0;

  const set = <K extends keyof AdminAggregateFilters>(key: K, value: AdminAggregateFilters[K]) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const groupHeader = (() => {
    switch (data?.groupBy) {
      case "day":
        return "Day";
      case "category":
        return "Category";
      case "model":
        return "Model";
      case "tool":
        return "App";
      case "user":
        return "User";
      case "country":
        return "Country";
      default:
        return "Group";
    }
  })();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Spend</h1>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-muted/30 p-4">
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          From
          <Input
            type="date"
            value={filters.from ?? ""}
            onChange={(e) => set("from", e.target.value || undefined)}
            className="h-9"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          To
          <Input
            type="date"
            value={filters.to ?? ""}
            onChange={(e) => set("to", e.target.value || undefined)}
            className="h-9"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          Group by
          <select
            className={SELECT_CLASS}
            value={filters.groupBy ?? "category"}
            onChange={(e) => set("groupBy", e.target.value as AdminAggregateGroupBy)}
          >
            {GROUP_BY.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          Category
          <select
            className={SELECT_CLASS}
            value={filters.category ?? ""}
            onChange={(e) =>
              set("category", (e.target.value || undefined) as AdminAggregateFilters["category"])
            }
          >
            <option value="">All</option>
            <option value="llm">LLM</option>
            <option value="tools">Apps</option>
            <option value="infra">Workspace</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          Country
          <Input
            value={filters.country ?? ""}
            onChange={(e) => set("country", e.target.value || undefined)}
            placeholder="e.g. US"
            className="h-9 w-24"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          Has VM
          <select
            className={SELECT_CLASS}
            value={filters.hasVm ?? ""}
            onChange={(e) =>
              set("hasVm", (e.target.value || undefined) as AdminAggregateFilters["hasVm"])
            }
          >
            <option value="">Any</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          Min spend ($)
          <Input
            type="number"
            min={0}
            value={filters.minCents != null ? String(filters.minCents / 100) : ""}
            onChange={(e) =>
              set(
                "minCents",
                e.target.value ? Math.round(parseFloat(e.target.value) * 100) : undefined,
              )
            }
            className="h-9 w-24"
          />
        </label>
        <Button size="sm" onClick={() => void load(filters)} disabled={loading}>
          {loading ? "Loading..." : "Apply"}
        </Button>
      </div>

      {err && <p className="text-danger">{err}</p>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard label="Total cost" value={formatCents(data?.totals.costCents ?? 0)} />
        <KpiCard label="USD cost" value={formatUsdCost(data?.totals.usdCost ?? 0)} />
        <KpiCard label="Credits" value={formatCredits(data?.totals.credits ?? 0)} />
        <KpiCard label="Total price" value={formatCents(data?.totals.priceCents ?? 0)} />
        <KpiCard label="Margin" value={formatCents(margin)} />
        <KpiCard label="Users" value={String(data?.totals.userCount ?? 0)} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Daily spend (cost)</CardTitle>
        </CardHeader>
        <CardContent>
          <DailyStackedChart data={dailyData} series={DAILY_SERIES} formatValue={formatCents} />
        </CardContent>
      </Card>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-muted/50 text-muted-fg">
            <tr>
              <th className="px-4 py-3 font-medium">{groupHeader}</th>
              <th className="px-4 py-3 font-medium">Cost</th>
              <th className="px-4 py-3 font-medium">USD cost</th>
              <th className="px-4 py-3 font-medium">Credits</th>
              <th className="px-4 py-3 font-medium">Price</th>
              <th className="px-4 py-3 font-medium">Margin</th>
            </tr>
          </thead>
          <tbody>
            {(data?.groups ?? []).map((g) => (
              <tr key={g.key} className="border-b border-border last:border-0">
                <td className="px-4 py-3">
                  {data?.groupBy === "user" ? (
                    <Link to={`/users/${g.key}`} className="text-accent hover:underline">
                      {g.label ?? g.key}
                    </Link>
                  ) : (
                    <span className="font-mono text-xs">{g.label ?? g.key}</span>
                  )}
                </td>
                <td className="px-4 py-3 tabular-nums">{formatCents(g.costCents)}</td>
                <td className="px-4 py-3 tabular-nums">{formatUsdCost(g.usdCost)}</td>
                <td className="px-4 py-3 tabular-nums">{formatCredits(g.credits)}</td>
                <td className="px-4 py-3 tabular-nums">{formatCents(g.priceCents)}</td>
                <td className="px-4 py-3 tabular-nums">{formatCents(g.priceCents - g.costCents)}</td>
              </tr>
            ))}
            {data && data.groups.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-muted-fg">
                  No spend matches these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
